import React from 'react';

// Small presentational kit built on the warm semantic tokens (surface / border /
// on-surface) from styles/colors.css, so cards adapt to light & dark automatically.

export const Card: React.FC<{
  className?: string;
  id?: string;
  children: React.ReactNode;
}> = ({ className = '', id, children }) => (
  <div
    id={id}
    className={'rounded-xl border border-theme-border bg-surface p-5 ' + className}
    style={{ boxShadow: 'var(--color-card-edge)' }}
  >
    {children}
  </div>
);

export const CardTitle: React.FC<{
  title: string;
  source?: string;
  right?: React.ReactNode;
}> = ({ title, source, right }) => (
  <div className='mb-3 flex items-start justify-between gap-2'>
    <div>
      <h3 className='text-[13px] font-semibold text-on-surface'>{title}</h3>
      {source && <div className='mt-0.5 text-[11px] text-on-surface-tertiary'>{source}</div>}
    </div>
    {right}
  </div>
);

export const KpiCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  muted?: boolean;
  id?: string;
}> = ({ label, value, sub, muted, id }) => (
  <Card id={id} className='scroll-mt-20'>
    <div className='text-[10px] font-semibold uppercase tracking-[0.14em] text-on-surface-tertiary'>
      {label}
    </div>
    <div
      className={
        'mt-2 text-3xl font-semibold tracking-tight ' +
        (muted ? 'text-on-surface-quaternary' : 'text-on-surface')
      }
      style={{ fontFamily: "'Spectral', Georgia, serif" }}
    >
      {value}
    </div>
    {sub && <div className='mt-1 text-xs text-on-surface-tertiary'>{sub}</div>}
  </Card>
);

export const Pill: React.FC<{
  tone: 'ok' | 'warn' | 'info';
  children: React.ReactNode;
}> = ({ tone, children }) => {
  const tones: Record<string, string> = {
    ok: 'bg-green-100 text-green-800',
    warn: 'bg-yellow-100 text-yellow-800',
    info: 'bg-blue-100 text-blue-800',
  };
  return <span className={'rounded-full px-2 py-0.5 text-[10px] ' + tones[tone]}>{children}</span>;
};

export const Skeleton: React.FC<{
  className?: string;
  style?: React.CSSProperties;
}> = ({ className = '', style }) => (
  <div className={'animate-pulse rounded bg-surface-tertiary ' + className} style={style} />
);

// The sign goes BEFORE the currency symbol: -$1,234, not $-1,234. Formatting the
// magnitude and prefixing the sign ourselves keeps that true in both the compact
// and full branches. Negative amounts became reachable with the Anchorage flow
// panels (net flow, and net flow per advisor, both of which go negative when
// withdrawals exceed deposits). Note -0 formats as "$0", not "-$0", since
// `-0 < 0` is false.
export const usd = (n: number, compact = false): string => {
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  return (
    sign +
    '$' +
    (compact
      ? Intl.NumberFormat('en-US', {
          notation: 'compact',
          maximumFractionDigits: 1,
        }).format(abs)
      : Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(abs))
  );
};
